import { useState, useEffect, useCallback } from 'react';
import { BigNumber, ContractTransaction, ethers, utils } from 'ethers';
import { useWallet } from 'components/WalletContext';
import { parseEther } from 'ethers/lib/utils';
import { getEtherscanLink } from 'utils/helpers';
import {
  EulerBeatsAbi as EulerBeatsV1,
  EulerBeatsV1Factory,
  PrintingPressAbi as PrintingPress,
  PrintingPressFactory,
} from '../contracts/genesis';
import { checkDeployment } from 'utils/contractHelpers';
import { DROP_CONFIG } from 'components/Drops/dropConfig';
import useLegacyIsRightNetwork from './useLegacyIsRightNetwork';

let interval: any;

const MINT_GAS_LIMIT = 220000;
const BURN_GAS_LIMIT = 200000;

const useV1Contracts = () => {
  const [loading, setLoading] = useState(false);
  const { address, web3Provider, notify } = useWallet();
  const isRightNetwork = useLegacyIsRightNetwork();

  const [eb, setEb] = useState<EulerBeatsV1>();
  const [printingPress, setPrintingPress] = useState<PrintingPress>();
  const [contractsReady, setContractsReady] = useState<boolean>(false);

  // Printing Press approval
  const [isPressApproved, setIsPressApproved] = useState<boolean>(false);

  // Print/burn enabled
  const [printIsEnabled, setPrintIsEnabled] = useState<boolean>(false);
  const [burnIsEnabled, setBurnIsEnabled] = useState<boolean>(false);

  // ===============================================================================================
  // GETTERS
  // ===============================================================================================
  const contractAddress = DROP_CONFIG.genesis.contractAddress;
  const printingPressContractAddress = DROP_CONFIG.genesis.printingPressContractAddress;

  // SETUP CONTRACTS
  useEffect(() => {
    async function loadContracts() {
      setLoading(true);

      try {
        if (web3Provider && address && contractAddress) {
          console.log('Loading V1 contracts');
          const isEBContract = await checkDeployment(web3Provider, contractAddress);
          const isPPContract = await checkDeployment(web3Provider, printingPressContractAddress);

          if (!isEBContract) throw Error('The provided genesis address is not a contract');
          if (!isPPContract) throw Error('The provided printing press address is not a contract');

          const eulerBeats = EulerBeatsV1Factory.connect(contractAddress, web3Provider.getSigner());
          setEb(eulerBeats);

          const printingPress = PrintingPressFactory.connect(
            printingPressContractAddress,
            web3Provider.getSigner()
          );
          setPrintingPress(printingPress);

          const approved = await eulerBeats.isApprovedForAll(address, printingPressContractAddress);
          setIsPressApproved(approved);

          const printingEnabled = await isPrintingEnabled(printingPress);
          const burningEnabled = await isBurningEnabled(printingPress);

          setPrintIsEnabled(printingEnabled);
          setBurnIsEnabled(burningEnabled);

          setContractsReady(true);
        }
      } catch (e) {
        console.log('ERROR LOADING V1 CONTRACTS:', e);
      } finally {
        setLoading(false);
      }
    }

    loadContracts();
  }, [web3Provider, address, contractAddress, printingPressContractAddress]);

  // NOT BEING USED
  // Get printPrice and burnPrice for an original NFT
  const getPrintSupplyAndPrices = useCallback(
    async (originalId: string) => {
      if (!eb || !isRightNetwork || !originalId) throw new Error('contracts not setup yet');
      const printsSupply = await eb.seedToPrintsSupply(originalId);

      const nextPrintPrice = await eb.getPrintPrice(printsSupply.add('1'));
      const nextPrintPriceInEth = utils.formatEther(nextPrintPrice.toString());

      const burnPrice = await eb.getBurnPrice(printsSupply);
      const burnPriceInEth = utils.formatEther(burnPrice.toString());

      const nextBurnPrice = await eb.getBurnPrice(printsSupply.add('1'));
      const nextBurnPriceInEth = utils.formatEther(nextBurnPrice.toString());

      return {
        printsSupply,
        printPrice: nextPrintPriceInEth,
        nextPrintPrice: nextPrintPriceInEth,
        burnPrice: burnPriceInEth,
        nextBurnPrice: nextBurnPriceInEth,
      };
    },
    [eb, isRightNetwork]
  );

  // NOT BEING USED
  // Get printPrice and burnPrices for multiple original NFTs
  // const getPrintSuppliesAndPrices = useCallback(
  //   async (originals: any[]): Promise<EulerBeat[]> => {
  //     return Promise.all(
  //       originals.map(async (originalNft: EulerBeat) => {
  //         const {
  //           printsSupply,
  //           printPrice,
  //           burnPrice,
  //           nextBurnPrice,
  //         } = (await getPrintSupplyAndPrices(originalNft.id)) as PrintSupplyAndPrices;

  //         return {
  //           ...originalNft,
  //           numPrints: printsSupply.toNumber(),
  //           printPrice,
  //           burnPrice,
  //           nextBurnPrice,
  //           rarity: getRarity(originalNft),
  //         };
  //       })
  //     );
  //   },
  //   [getPrintSupplyAndPrices]
  // );

  // ORIGINAL OWNER
  const getOriginalOwnerInfo = useCallback(
    async originalId => {
      if (!eb || !isRightNetwork || !originalId) throw new Error('contracts not setup yet');
      const originalOwner = await eb.seedToOwner(originalId);
      const originalOwnerBalance = await eb.balanceOf(originalOwner, originalId);
      return {
        originalOwner,
        originalOwnerBalance: originalOwnerBalance.toString(),
      };
    },
    [eb, isRightNetwork]
  );

  // Query contracts to get relevant price and supply data for PRINTS
  const getOwnedPricesByOriginalId = useCallback(
    async (originalId: string, account: string) => {
      if (!eb || !isRightNetwork || !originalId) throw new Error('contracts not setup yet');

      // Get printId of original: each original has 1 id (originalId) and 1 printId
      const printId = await eb.getPrintTokenIdFromSeed(originalId);
      // PRINTS SUPPLY OF USER
      const ownedPrintsSupply = await eb.balanceOf(account, printId);

      // FAIL FAST IF ACCOUNT DOES NOT OWN ANY PRINTS
      if (ownedPrintsSupply.eq('0')) {
        return false;
      }

      // TOTAL PRINTS SUPPLY
      const totalPrintsSupply = await eb.seedToPrintsSupply(originalId);

      // PRICES
      const nextPrintPrice = await eb.getPrintPrice(totalPrintsSupply.add('1'));
      const nextPrintPriceInEth = utils.formatEther(nextPrintPrice.toString());
      const burnPrice = await eb.getBurnPrice(totalPrintsSupply);
      const burnPriceInEth = utils.formatEther(burnPrice.toString());
      const nextBurnPrice = await eb.getBurnPrice(totalPrintsSupply.add('1'));
      const nextBurnPriceInEth = utils.formatEther(nextBurnPrice.toString());

      // ORIGINAL OWNER
      // const {
      //   originalOwner,
      //   originalOwnerBalance,
      //   originalOwnerPrintBalance,
      // } = (await getOriginalOwnerInfo(originalId) as any);

      return {
        type: 'print',
        // IDs
        originalId,
        printId: printId.toString(),

        // ORIGINAL OWNER
        // originalOwner,
        // originalOwnerBalance,
        // originalOwnerPrintBalance,

        // PRINTS SUPPLY
        printsSupply: totalPrintsSupply.toString(),
        totalPrintsSupply: totalPrintsSupply.toString(), // same thing as printsSupply
        ownedPrintsSupply: ownedPrintsSupply.toString(),
        // PRINT PRICES
        printPrice: nextPrintPriceInEth,
        burnPrice: burnPriceInEth,
        nextBurnPrice: nextBurnPriceInEth,
      };
    },
    [eb, isRightNetwork]
  );

  // Query contracts to get relevant price and supply data for PRINTS
  const getPricesByOriginalId = useCallback(
    async (originalId: string) => {
      if (!eb || !isRightNetwork || !originalId) throw new Error('contracts not setup yet');
      const printsSupply = await eb.seedToPrintsSupply(originalId);
      const nextPrintPrice = await eb.getPrintPrice(printsSupply.add('1'));
      const nextPrintPriceInEth = utils.formatEther(nextPrintPrice);
      const burnPrice = await eb.getBurnPrice(printsSupply);
      const burnPriceInEth = utils.formatEther(burnPrice);
      const nextBurnPrice = await eb.getBurnPrice(printsSupply.add('1'));
      const nextBurnPriceInEth = utils.formatEther(nextBurnPrice);

      const prices = {
        printsSupply: printsSupply.toString(),
        printPrice: nextPrintPriceInEth,
        burnPrice: burnPriceInEth,
        nextBurnPrice: nextBurnPriceInEth,
      };
      return prices;
    },
    [eb, isRightNetwork]
  );

  const getBalanceOfBatch = useCallback(
    async (accounts: string[], tokenIds: string[]) => {
      if (!eb || !isRightNetwork) throw new Error('contracts not setup yet');

      if (accounts.length === 1 && tokenIds.length > 1) {
        console.log('Getting balances for address', accounts[0]);
        accounts = tokenIds.map(i => accounts[0]);
      }

      return await eb.balanceOfBatch(accounts, tokenIds);
    },
    [eb, isRightNetwork]
  );

  async function isPrintingEnabled(contract: PrintingPress) {
    const isEnabled = await contract.printEnabled();
    console.log('Printing is enabled:', isEnabled);
    return isEnabled;
  }

  async function isBurningEnabled(contract: PrintingPress) {
    const isEnabled = await contract.burnEnabled();
    console.log('Burning is enabled:', isEnabled);
    return isEnabled;
  }

  // ===============================================================================================
  // TRANSACTIONS
  // ===============================================================================================
  function handleEmitter(res: ContractTransaction, cb: any) {
    const { emitter } = notify.hash(res.hash);

    emitter.on('txConfirmed', (tx: any) => {
      console.log('tx:', tx);
      if (tx.status === 'confirmed') {
        const link = getEtherscanLink(tx.hash);
        // Triggers success modal
        cb({ link, ...tx });
      }
      return {
        type: 'success',
        message:
          'Your transaction has succeeded. The UI may take a few moments to reflect these changes.',
        autoDismiss: 10000,
      };
    });
  }

  // MINT ORIGINAL
  async function mintOriginal(cb: any) {
    if (!eb || !isRightNetwork) return;

    const res = await eb.mint({
      value: parseEther('0.271'),
      gasLimit: MINT_GAS_LIMIT,
    });
    handleEmitter(res, cb);
  }

  // CREATE PRINT
  async function createPrint(
    originalId: string,
    originalOwner: string,
    allowedSlippage: number | string,
    onWrongOwner: any,
    cb: any
  ) {
    if (!printingPress || !eb || !isRightNetwork || !originalId)
      throw new Error('contracts not setup correctly');

    const maxPrintPrice = await getPriceWithSlippage(originalId, allowedSlippage);

    console.log(`Tokenid : ${originalId} Original owner: ${originalOwner}`);

    // Check that originalOwner is correct
    const originalOwnerBalance = await eb.balanceOf(originalOwner, originalId);

    // This means the token was recently tranferred and our api doesn't have the updated address yet
    if (originalOwnerBalance.toString() === '0') {
      onWrongOwner(originalId);
      return;
    }

    const res = await printingPress.mintPrint(originalId, originalOwner, {
      value: maxPrintPrice!.toString(),
      gasLimit: MINT_GAS_LIMIT,
    });
    handleEmitter(res, cb);
  }

  // BURN PRINT
  async function burnPrint(originalId: string, allowedSlippage: number, cb: any) {
    if (!printingPress || !eb || !isRightNetwork || !originalId)
      throw new Error('contracts not setup correctly');

    const printsSupply = await eb.seedToPrintsSupply(originalId);
    const minimumSupply = getMinimumSupplyForBurn(printsSupply, allowedSlippage);

    const res = await printingPress.burnPrint(originalId, minimumSupply, {
      gasLimit: BURN_GAS_LIMIT,
    }); // It will burn no matter what refund amount is
    handleEmitter(res, cb);
  }

  // GET PRICE WITH SLIPPAGE
  async function getPriceWithSlippage(seed: string, allowedSlippage: number | string) {
    if (!eb || !isRightNetwork || !seed) throw new Error('contracts not setup correctly');

    if (typeof allowedSlippage === 'string') {
      return ethers.utils.parseEther(allowedSlippage);
    }

    const printsSupply = await eb.seedToPrintsSupply(seed);
    const allowedPrintsSupply = printsSupply.add(allowedSlippage);
    const maxPrintPrice = await eb.getPrintPrice(allowedPrintsSupply.add('1'));

    return maxPrintPrice;
  }

  // GET BURN PRICE WITH SLIPPAGE
  async function getBurnPriceWithSlippage(seed: string, allowedSlippage: number) {
    if (!eb || !isRightNetwork || !seed) return;

    const printsSupply = await eb.seedToPrintsSupply(seed);

    const allowedPrintsSupply = getMinimumSupplyForBurn(printsSupply, allowedSlippage);
    const actualBurnPrice = await eb.getBurnPrice(allowedPrintsSupply);
    return actualBurnPrice;
  }

  function getMinimumSupplyForBurn(printsSupply: BigNumber, allowedSlippage: number) {
    if (printsSupply.toNumber() - allowedSlippage <= 1) {
      return ethers.BigNumber.from(1);
    } else {
      return printsSupply.sub(allowedSlippage);
    }
  }

  const approve = async (operator: string, cb: any) => {
    if (!eb || !isRightNetwork) throw new Error('contracts not setup correctly');
    if (!address) return;

    // check approval
    const approved = await checkApproval(address!, operator);

    if (approved) {
      return cb(true);
    }

    console.log(`Approving ${operator}`);
    const res = await eb.setApprovalForAll(operator, true);
    handleEmitter(res, cb);
  };

  const checkApproval = async (account: string, operator: string) => {
    if (!eb || !isRightNetwork) throw new Error('contracts not setup correctly');
    const approved = await eb.isApprovedForAll(account, operator);
    return approved;
  };

  const approvePrintingPress = async () => {
    if (!eb || !isRightNetwork) throw new Error('contracts not setup correctly');

    console.log(`Approving ${printingPressContractAddress}`);
    const res = await eb.setApprovalForAll(printingPressContractAddress, true);

    handleEmitter(res, async (values: any) => {
      console.log('success', values);
      if (address) {
        interval = setInterval(async () => {
          let approved = await eb.isApprovedForAll(address, printingPressContractAddress);
          console.log({ address, printingPressContractAddress, approved });
          setIsPressApproved(approved);
          if (approved) {
            clearInterval(interval);
          }
        }, 1000);
      }
    });
  };

  return {
    mintOriginal,
    burnPrint,
    createPrint,
    getPrintSupplyAndPrices,
    // getPrintSuppliesAndPrices,
    getOriginalOwnerInfo,
    getOwnedPricesByOriginalId,
    getPricesByOriginalId,
    getPriceWithSlippage,
    getBurnPriceWithSlippage,
    isPressApproved,
    approvePrintingPress,
    contractsReady,
    getBalanceOfBatch,
    printIsEnabled,
    burnIsEnabled,
    loading,
    approve,
    checkApproval,
  };
};

export default useV1Contracts;
